import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Camera, Upload, User, Mail, X, RotateCcw, Check, AlertCircle } from 'lucide-react';

// Simulación de react-webcam para el entorno de artifacts
const Webcam = ({
    ref,
    screenshotFormat,
    videoConstraints,
    onUserMedia,
    onUserMediaError,
    className,
    style
}: any) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const streamRef = useRef<MediaStream | null>(null);

    useEffect(() => {
        const startWebcam = async () => {
            try {
                const constraints = {
                    video: videoConstraints?.video || videoConstraints || { width: 640, height: 480 },
                    audio: false
                };

                const stream = await navigator.mediaDevices.getUserMedia(constraints);
                streamRef.current = stream;
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                    videoRef.current.play();
                }
                if (onUserMedia) onUserMedia(stream);
            } catch (error) {
                if (onUserMediaError) onUserMediaError(error);
            }
        };

        startWebcam();

        return () => {
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop());
            }
        };
    }, [videoConstraints, onUserMedia, onUserMediaError]);

    // Exponer método getScreenshot al ref
    React.useImperativeHandle(ref, () => ({
        getScreenshot: () => {
            if (videoRef.current) {
                const canvas = document.createElement('canvas');
                const video = videoRef.current;
                canvas.width = video.videoWidth;
                canvas.height = video.videoHeight;
                const ctx = canvas.getContext('2d');
                if (ctx) {
                    ctx.drawImage(video, 0, 0);
                    return canvas.toDataURL(screenshotFormat || 'image/jpeg', 0.8);
                }
            }
            return null;
        }
    }));

    return (
        <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className={className}
            style={style}
        />
    );
};

interface RegistrationData {
    nombres: string;
    apellidos: string;
    email: string;
    fotografia: string | null;
}

const RegistrationForm: React.FC = () => {
    const [formData, setFormData] = useState<RegistrationData>({
        nombres: '',
        apellidos: '',
        email: '',
        fotografia: null
    });

    const [showCamera, setShowCamera] = useState(false);
    const [isCameraReady, setIsCameraReady] = useState(false);
    const [cameraError, setCameraError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [facingMode, setFacingMode] = useState<'user' | 'environment'>('user');

    const webcamRef = useRef<any>(null);
    const videoRef = useRef<HTMLVideoElement>(null);
    // const streamRef = useRef<MediaStream | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Detectar si es dispositivo móvil
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    // const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    // const isAndroid = /Android/.test(navigator.userAgent);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file && file.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onload = (event) => {
                setFormData(prev => ({
                    ...prev,
                    fotografia: event.target?.result as string
                }));
            };
            reader.readAsDataURL(file);
        }
    };

    const startCamera = useCallback(async () => {
        setIsLoading(true);
        setCameraError(null);
        setShowCamera(true);
    }, []);

    const handleUserMedia = useCallback(() => {
        setIsCameraReady(true);
        setIsLoading(false);
    }, []);

    const handleUserMediaError = useCallback((error: any) => {
        console.error('Error al acceder a la cámara:', error);
        setIsLoading(false);

        if (error.name === 'NotAllowedError') {
            setCameraError('Permiso de cámara denegado. Por favor, permite el acceso a la cámara.');
        } else if (error.name === 'NotFoundError') {
            setCameraError('No se encontró una cámara en este dispositivo.');
        } else if (error.name === 'OverconstrainedError') {
            setCameraError('La configuración de cámara solicitada no está disponible.');
        } else {
            setCameraError(`Error al acceder a la cámara: ${error.message || 'Error desconocido'}`);
        }
    }, []);

    const stopCamera = useCallback(() => {
        setShowCamera(false);
        setIsCameraReady(false);
        setCameraError(null);
    }, []);

    const capturePhoto = useCallback(() => {
        if (webcamRef.current && isCameraReady) {
            const imageSrc = webcamRef.current.getScreenshot();
            if (imageSrc) {
                setFormData(prev => ({
                    ...prev,
                    fotografia: imageSrc
                }));
                stopCamera();
            }
        }
    }, [isCameraReady, stopCamera]);

    const switchCamera = useCallback(() => {
        if (isMobile) {
            setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
            stopCamera();
            setTimeout(() => startCamera(), 100);
        }
    }, [isMobile, startCamera, stopCamera]);

    const removePhoto = () => {
        setFormData(prev => ({
            ...prev,
            fotografia: null
        }));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.nombres || !formData.apellidos || !formData.email) {
            alert('Por favor, completa todos los campos obligatorios');
            return;
        }

        alert('Registro completado exitosamente!');
    };

    // Limpiar al desmontar componente
    useEffect(() => {
        return () => {
            // La limpieza se maneja automáticamente por el componente Webcam
        };
    }, []);

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-0 md:p-4">
            <div className="bg-white w-full h-full md:h-auto md:max-w-2xl md:rounded-2xl md:shadow-2xl overflow-hidden">
                {/* Header */}
                <div className="bg-red-700 text-white p-6 md:p-8">
                    <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-3">
                        <User className="w-8 h-8" />
                        Asistente Virtual
                    </h1>
                    <p className="text-blue-100 mt-2">Complete sus datos para crear una cuenta</p>
                </div>

                <div className="p-6 md:p-8">
                    {/* Camera Modal */}
                    {showCamera && (
                        <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center">
                            <div className="relative w-full h-full md:w-auto md:h-auto md:max-w-md">
                                <div className="absolute top-4 right-4 z-10 flex gap-2">
                                    {isMobile && (
                                        <button
                                            onClick={switchCamera}
                                            className="bg-white bg-opacity-20 text-white p-2 rounded-full hover:bg-opacity-30 transition-all"
                                            title={`Cambiar a cámara ${facingMode === 'user' ? 'trasera' : 'frontal'}`}
                                        >
                                            <RotateCcw className="w-5 h-5" />
                                        </button>
                                    )}
                                    <button
                                        onClick={stopCamera}
                                        className="bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition-colors"
                                    >
                                        <X className="w-5 h-5" />
                                    </button>
                                </div>

                                <div className="relative">
                                    {isLoading && (
                                        <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 z-10">
                                            <div className="text-white text-center">
                                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-2"></div>
                                                <p>Iniciando cámara...</p>
                                            </div>
                                        </div>
                                    )}

                                    {!isMobile ? (
                                        // Para PC: usar react-webcam simulado
                                        <Webcam
                                            ref={webcamRef}
                                            screenshotFormat="image/jpeg"
                                            videoConstraints={{
                                                width: 640,
                                                height: 480,
                                                facingMode: 'user'
                                            }}
                                            onUserMedia={handleUserMedia}
                                            onUserMediaError={handleUserMediaError}
                                            className="w-full h-full md:w-96 md:h-72 object-cover rounded-lg"
                                        />
                                    ) : (
                                        // Para móvil: usar video nativo con configuraciones avanzadas
                                        <video
                                            ref={videoRef}
                                            autoPlay
                                            playsInline
                                            muted
                                            controls={false}
                                            className="w-full h-full md:w-96 md:h-72 object-cover rounded-lg"
                                            style={{ transform: facingMode === 'user' ? 'scaleX(-1)' : 'none' }}
                                        />
                                    )}

                                    {isCameraReady && (
                                        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2">
                                            <button
                                                onClick={capturePhoto}
                                                className="bg-blue-600 hover:bg-blue-700 text-white p-4 rounded-full shadow-lg transition-colors"
                                            >
                                                <Camera className="w-6 h-6" />
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {cameraError && (
                                    <div className="absolute bottom-4 left-4 right-4 bg-red-500 text-white p-3 rounded-lg">
                                        <div className="flex items-center gap-2">
                                            <AlertCircle className="w-5 h-5" />
                                            <span className="text-sm">{cameraError}</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    <canvas className="hidden" />

                    <div className="space-y-6">
                        {/* Campos de texto */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    <User className="w-4 h-4 inline mr-2" />
                                    Nombres *
                                </label>
                                <input
                                    type="text"
                                    name="nombres"
                                    value={formData.nombres}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                    placeholder="Ingrese sus nombres"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    <User className="w-4 h-4 inline mr-2" />
                                    Apellidos *
                                </label>
                                <input
                                    type="text"
                                    name="apellidos"
                                    value={formData.apellidos}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                    placeholder="Ingrese sus apellidos"
                                    required
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                <Mail className="w-4 h-4 inline mr-2" />
                                Email *
                            </label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                placeholder="correo@ejemplo.com"
                                required
                            />
                        </div>

                        {/* Sección de fotografía */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-4">
                                <Camera className="w-4 h-4 inline mr-2" />
                                Fotografía
                            </label>

                            {formData.fotografia ? (
                                <div className="relative w-full max-w-sm mx-auto">
                                    <img
                                        src={formData.fotografia}
                                        alt="Fotografía del usuario"
                                        className="w-full h-64 object-cover rounded-lg border-2 border-gray-200"
                                    />
                                    <button
                                        type="button"
                                        onClick={removePhoto}
                                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <button
                                            type="button"
                                            onClick={() => fileInputRef.current?.click()}
                                            className="flex items-center justify-center gap-2 w-full p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all duration-200"
                                        >
                                            <Upload className="w-5 h-5 text-gray-500" />
                                            <span className="text-gray-600">Subir archivo</span>
                                        </button>

                                        <button
                                            type="button"
                                            onClick={startCamera}
                                            disabled={isLoading}
                                            className="flex items-center justify-center gap-2 w-full p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all duration-200 disabled:opacity-50"
                                        >
                                            <Camera className="w-5 h-5 text-gray-500" />
                                            <span className="text-gray-600">
                                                {isLoading ? 'Iniciando...' : 'Tomar foto'}
                                            </span>
                                        </button>
                                    </div>

                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*"
                                        onChange={handleFileUpload}
                                        className="hidden"
                                    />




                                </div>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-4">

                                He leído y acepto la política de privacidad
                            </label>
                        </div>

                        {/* Botón de envío */}
                        <button
                            type="button"
                            onClick={(e) => {
                                e.preventDefault();
                                handleSubmit(e as any);
                            }}
                            className="w-full bg-red-600  hover:bg-red-500  text-white font-semibold py-4 px-6 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 shadow-lg"
                        >
                            <Check className="w-5 h-5" />
                            Completar Registro
                        </button>

                        <p className="text-xs text-gray-500 text-start">
                            * La información solicitada será puesta a revisión del personal de santander para su posterior aprobación.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RegistrationForm;